import type { FC, ReactElement } from 'react';

import { Permission } from '@/api/permissions';
import EventsIcon from '@/assets/svg/sidebar/events.svg';
import InvestingBoardIcon from '@/assets/svg/sidebar/layout-dashboard.svg';
import { FeatureFlagName, FeatureFlags } from '@/global-hooks/use-feature-flag-enabled';
import type { UsePermissionsResult } from '@/shared/modules/permissions';

import { EntitiesTab } from './_entities-tab';
import { FundsTab } from './_funds-tab';
import { UsersTab } from './_user-tab';
import { AnalyticsTab } from './analytics-tab';
import ActivityIcon from './assets/activity.svg';
import BriefcaseIcon from './assets/briefcase.svg';
import ChartLineIcon from './assets/chart-line.svg';
import CoinsIcon from './assets/coins.svg';
import UserIcon from './assets/user.svg';
import { EventsAccessTab } from './events-access-tab';
import { InvestingBoardTab } from './investing-board-tab';
import { TeamActivityTab } from './team-activity-tab';

export const overviewTabKeys = {
  Users: 'users',
  Entities: 'entities',
  Funds: 'funds',
  Analytics: 'analytics',
  TeamActivity: 'team-activity',
  EventsAccess: 'events-access',
  InvestingBoard: 'investing-board',
} as const;

/**
 * Typed rather than inferred so `useOpenFeatures` still accepts it: without the annotation
 * TypeScript infers a shape with no `featureFlag` key on the tabs that do not carry one.
 */
type OverviewTab = {
  value: string;
  label: string;
  icon: ReactElement;
  component: FC;
  featureFlag?: FeatureFlagName;
  permission?: Permission;
  visibleTo?: (_checks: Pick<UsePermissionsResult, 'has'>) => boolean;
};

export const isOverviewTabVisible = (
  tab: Pick<OverviewTab, 'permission' | 'visibleTo'>,
  checks: Pick<UsePermissionsResult, 'has'>,
): boolean => {
  if (tab.visibleTo) return tab.visibleTo(checks);
  return !tab.permission || checks.has(tab.permission);
};

export const isTeamActivityVisible = (checks: Pick<UsePermissionsResult, 'has'>): boolean =>
  checks.has(Permission.AdminTeamActivityView) || checks.has(Permission.AddressBookManage);

export const overviewTabs: OverviewTab[] = [
  {
    value: overviewTabKeys.Users,
    label: 'Users',
    icon: <UserIcon />,
    component: UsersTab,
    permission: Permission.UserAdminView,
  },
  {
    value: overviewTabKeys.Entities,
    label: 'Entities',
    icon: <BriefcaseIcon />,
    component: EntitiesTab,
    permission: Permission.EntityAdminView,
  },
  {
    value: overviewTabKeys.Funds,
    label: 'Funds',
    icon: <CoinsIcon />,
    component: FundsTab,
    permission: Permission.FundAdminView,
  },
  {
    value: overviewTabKeys.Analytics,
    label: 'Analytics',
    icon: <ChartLineIcon />,
    component: AnalyticsTab,
    permission: Permission.AdminConsoleView,
  },
  {
    value: overviewTabKeys.TeamActivity,
    label: 'Team activity',
    icon: <ActivityIcon />,
    component: TeamActivityTab,
    visibleTo: isTeamActivityVisible,
  },
  {
    // Stands where the Events Creation and Manage Center used to, event management itself having
    // moved to the Managing Events subtab. Only the two top Gamma roles hold the permission.
    value: overviewTabKeys.EventsAccess,
    label: 'Events access',
    icon: <EventsIcon />,
    component: EventsAccessTab,
    permission: Permission.GammaManageEventsAccess,
  },
  {
    value: overviewTabKeys.InvestingBoard,
    label: 'Investing board',
    icon: <InvestingBoardIcon />,
    component: InvestingBoardTab,
    featureFlag: FeatureFlags.INVESTING_FLOW,
    permission: Permission.KanbanItemView,
  },
];
